import { useMemo } from "react";
import Table, {
  TableControls,
  TableFooter,
  buildTwoLineColumn,
  buildActionColumn,
  buildCopyTextColumn,
  buildBulkSelectionColumn
} from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import Button from "@/components/Button";
import Tooltip from "@/components/Tooltip";
import { Path, useNavigate } from "@/router";
import { ColumnDef } from "@tanstack/react-table";
import SearchInput from "@/components/SearchInput";
import MultiSelect from "@/components/MultiSelect";
import { reject, isEmpty } from "lodash";
import { FacilitySearchResult, FacilitiesSearchQueryResponse } from "../queries";
import { generatePath } from "react-router-dom";
import { actionId, type TableRowMenu } from "@/hooks/useTableRowMenu";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { renderEmptyCellState } from "@/components/Table/utils";
import { getFacilityNameSecondaryText } from "../_utils/facilityNameSecondaryText";
import { formatFacilityPhone } from "../_utils/formatFacilityPhone";
import ListTableCopyButton from "@/pages/_components/ListTableCopyButton";
import { FACILITY_MERGE_COUNT } from "../facility-merge-purge/constants";
import type { Action } from "@/components/MenuButton";

function buildColumns(
  facilities: FacilitySearchResult[],
  setMenu: (menu: TableRowMenu) => void,
  isMobileView: boolean
): ColumnDef<FacilitySearchResult, any>[] {
  const nameColumn = buildTwoLineColumn<FacilitySearchResult>({
    accessorKey: "name",
    header: "Name",
    getPrimaryText: (row) => row.name,
    getSecondaryText: getFacilityNameSecondaryText,
    size: 200
  });

  return [
    {
      ...nameColumn,
      id: "name",
      enableSorting: false,
      meta: {
        ...nameColumn.meta,
        className: getStickyColumnClasses(!!facilities?.length)
      }
    },
    ...(isMobileView
      ? [
          {
            accessorFn: (row: FacilitySearchResult) =>
              `${row.mainAddress?.address1 || ""} ${row.mainAddress?.address2 || ""}`.trim(),
            id: "mainAddress.address",
            header: "",
            enableSorting: false,
            cell: ({ getValue }: any) => renderEmptyCellState(getValue())
          },
          {
            accessorFn: (row: FacilitySearchResult) =>
              `${row.mainAddress?.city}, ${row.mainAddress?.state} ${row.mainAddress?.postalCode}`.trim(),
            id: "mainAddress.secondary",
            header: "",
            enableSorting: false,
            cell: ({ getValue }: any) => renderEmptyCellState(getValue())
          }
        ]
      : [
          {
            accessorFn: (row: FacilitySearchResult) =>
              `${row.mainAddress?.address1 || ""} ${row.mainAddress?.address2 || ""}`.trim(),
            header: "Primary Address",
            id: "mainAddress.address1",
            enableSorting: false,
            cell: ({ getValue }: any) => renderEmptyCellState(getValue())
          },
          {
            accessorFn: (row: FacilitySearchResult) => row.mainAddress?.city,
            header: "City",
            id: "mainAddress.city",
            enableSorting: false,
            cell: ({ getValue }: any) => renderEmptyCellState(getValue())
          },
          {
            accessorFn: (row: FacilitySearchResult) => row.mainAddress?.state,
            header: "State",
            id: "mainAddress.state",
            enableSorting: false,
            cell: ({ getValue }: any) => renderEmptyCellState(getValue())
          },
          {
            accessorFn: (row: FacilitySearchResult) => row.mainAddress?.postalCode,
            header: "Zip",
            id: "mainAddress.postalCode",
            enableSorting: false,
            cell: ({ getValue }: any) => renderEmptyCellState(getValue())
          }
        ]),
    buildCopyTextColumn<FacilitySearchResult>({
      accessorKey: "phone",
      header: "Phone",
      copyFieldLabel: "Phone",
      CopyButtonComponent: ListTableCopyButton,
      getText: (row) => formatFacilityPhone(row) || "",
      shouldShowCopyButton: (row) => !!formatFacilityPhone(row),
      enableSorting: false
    }),
    {
      accessorFn: (row) => row.parentCompany?.name,
      header: "Parent Company",
      id: "parentCompany.name",
      enableSorting: false,
      cell: ({ getValue }) => renderEmptyCellState(getValue())
    },
    {
      accessorFn: (row) => row.highestRankedEmployment?.person?.displayName,
      header: "Highest Ranking Member",
      id: "highestRankedEmployment.person.displayName",
      enableSorting: false,
      cell: ({ getValue }) => renderEmptyCellState(getValue())
    },
    buildActionColumn<FacilitySearchResult>({
      getActions: (row) => [
        {
          text: "View Facility",
          onClick: () => {
            const facilityPath = buildFacilityPath(row.id, "", isMobileView);
            window.location.href = facilityPath;
          }
        }
      ],
      actionId,
      setMenu
    })
  ];
}

type ChapterOption = {
  id: string;
  name: string;
  code: string;
};

function buildFacilityPath(id: string, searchParamsUrl: string, isMobile: boolean = false) {
  if (isMobile) {
    return `/facilities/${id}` as Path;
  }
  const searchParams = new URLSearchParams(searchParamsUrl);
  searchParams.delete("chapterIds[]");
  const filteredSearchParamsUrl = searchParams.toString();
  const returnToQuery = filteredSearchParamsUrl ? `&${filteredSearchParamsUrl}` : "";
  return generatePath(`/facilities/:id/details/contact?return_to=/facilities${returnToQuery}`, { id }) as Path;
}

export default function FacilitiesTable({
  loading,
  data,
  createHeaderPagination,
  createFooterPagination,
  searchValue,
  setSearchValue,
  clearSearch,
  addSearchParam,
  deleteSearchParams,
  searchParamsUrl,
  setMenu,
  selectedChapterIds,
  setSelectedChapterIds,
  selectedSectionIds,
  setSelectedSectionIds,
  chapters,
  chaptersLoading,
  sectionOptions,
  showArchived,
  setShowArchived,
  bulkSelection,
  actions = []
}: {
  loading: boolean;
  data?: FacilitiesSearchQueryResponse;
  createHeaderPagination: (data?: any, overrides?: any) => React.ReactElement;
  createFooterPagination: (data?: any, overrides?: any) => React.ReactElement;
  searchValue: string;
  setSearchValue: (value: string) => void;
  clearSearch: () => void;
  addSearchParam: (key: string, value: string) => void;
  deleteSearchParams: (key: string) => void;
  searchParamsUrl: string;
  setMenu: (menu: TableRowMenu) => void;
  selectedChapterIds: string[];
  setSelectedChapterIds: (ids: string[]) => void;
  selectedSectionIds: string[];
  setSelectedSectionIds: (ids: string[]) => void;
  chapters: ChapterOption[];
  chaptersLoading: boolean;
  sectionOptions: { value: string; label: string }[];
  showArchived: boolean;
  setShowArchived: (value: boolean) => void;
  bulkSelection?: ReturnType<typeof useBulkSelection>;
  actions?: Action[];
}) {
  const { isCompactLayout: isMobile } = useIsCompactLayout();
  const navigate = useNavigate();
  const isMergePurgeEnabled = !!bulkSelection;
  const rowSelection = bulkSelection?.rowSelection ?? {};
  const setRowSelection = bulkSelection?.setRowSelection;
  const bulkSelectionProps = bulkSelection?.bulkSelectionProps;
  const deselectAll = bulkSelection?.deselectAll;
  const rowSelectionCount = bulkSelection?.rowSelectionCount ?? 0;
  const orderedSelectedIds = bulkSelection?.orderedSelectedIds ?? [];
  const isSelectedOnly = bulkSelection?.isSelectedOnly ?? false;
  const isMergePurgeDisabled = rowSelectionCount !== FACILITY_MERGE_COUNT;

  const facilities = data?.facilities.nodes || [];
  const displayFacilities = isSelectedOnly ? facilities.filter((facility) => rowSelection[facility.id]) : facilities;
  const columns = useMemo(
    () => [
      ...(isMergePurgeEnabled ? [buildBulkSelectionColumn<FacilitySearchResult>()] : []),
      ...buildColumns(displayFacilities, setMenu, isMobile)
    ],
    [isMergePurgeEnabled, displayFacilities, setMenu, isMobile]
  );
  const bulkActions = isMergePurgeEnabled
    ? [
        {
          label: "Merge/Purge Facilities",
          variant: "outlined" as const,
          disabled: isMergePurgeDisabled,
          testId: "merge-purge-facilities-button",
          onClick: () => {
            navigate(`/facilities/facility-merge-purge?ids=${orderedSelectedIds.join(",")}` as Path);
          },
          render: isMergePurgeDisabled
            ? () => (
                <Tooltip
                  supportingText="Select exactly 2 facilities to merge"
                  variant="plain"
                  color="black"
                  positioning="fixed"
                >
                  <Button variant="outlined" disabled={true} onClick={() => {}} testId="merge-purge-facilities-button">
                    Merge/Purge Facilities
                  </Button>
                </Tooltip>
              )
            : undefined
        }
      ]
    : [];

  const chapterOptions = chapters.map((chapter) => ({
    value: chapter.id,
    label: `${chapter.name} (${chapter.code})`
  }));

  const filters = {
    search: !!searchValue,
    chapterIds: selectedChapterIds.length > 0,
    sectionIds: selectedSectionIds.length > 0
  };

  const handleClearAllFilters = () => {
    clearSearch();
    setSelectedChapterIds([]);
    setSelectedSectionIds([]);
    setShowArchived(false);
    navigate("/facilities", { replace: true });
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const filterChips = [
    {
      label: "Show Archived",
      onClick: () => setShowArchived(!showArchived),
      selected: showArchived
    }
  ];

  return (
    <Table
      data={displayFacilities}
      loading={loading}
      getRowLink={(row) => ({
        to: buildFacilityPath(row.original.id!, searchParamsUrl, isMobile) as any
      })}
      {...(isMergePurgeEnabled && {
        enableRowSelection: true,
        rowSelection,
        onRowSelectionChange: setRowSelection
      })}
      renderControls={(table) => (
        <TableControls
          pagination={createHeaderPagination(data?.facilities)}
          actions={actions}
          activeFilters={activeFilterCount}
          onClearAllFilters={handleClearAllFilters}
          useExpandedFilters={true}
          expandedFiltersState={filters}
          filterChips={filterChips}
          {...(isMergePurgeEnabled && {
            bulkActions,
            ...bulkSelectionProps,
            onDeselectAll: deselectAll,
            totalRowCount: rowSelectionCount,
            onToggleAllRows: (checked: boolean) => table.toggleAllRowsSelected(checked),
            allRowsSelected: table.getIsAllRowsSelected(),
            someRowsSelected: table.getIsSomeRowsSelected()
          })}
        >
          <SearchInput
            data-name="search"
            placeholder="Search Facilities"
            query={searchValue}
            onChange={setSearchValue}
            onClear={clearSearch}
          />
          <div data-name="sectionIds" data-filter-size="custom" className="min-w-[320px]">
            <MultiSelect
              label="Section"
              selectedValues={selectedSectionIds}
              options={sectionOptions}
              onSelect={(value, checked) => {
                const updatedIds = checked
                  ? [...selectedSectionIds, value]
                  : selectedSectionIds.filter((v) => v !== value);

                const filteredIds = reject(updatedIds, isEmpty);
                setSelectedSectionIds(filteredIds);
                if (filteredIds.length > 0) {
                  addSearchParam("sectionIds[]", filteredIds.join(","));
                  // Clear chapter selection when a new section is selected
                  if (selectedChapterIds.length > 0) {
                    setSelectedChapterIds([]);
                    deleteSearchParams("chapterIds[]");
                  }
                } else {
                  deleteSearchParams("sectionIds[]");
                }
              }}
              onClear={() => {
                setSelectedSectionIds([]);
                deleteSearchParams("sectionIds[]");
              }}
              className="w-full"
            />
          </div>
          <div data-name="chapterIds" data-filter-size="custom" className="min-w-[320px]">
            <MultiSelect
              label="Chapter"
              selectedValues={selectedChapterIds}
              options={chapterOptions}
              onSelect={(value, checked) => {
                const updatedIds = checked
                  ? [...selectedChapterIds, value]
                  : selectedChapterIds.filter((v) => v !== value);

                const filteredIds = reject(updatedIds, isEmpty);
                setSelectedChapterIds(filteredIds);
                if (filteredIds.length > 0) {
                  addSearchParam("chapterIds[]", filteredIds.join(","));
                } else {
                  deleteSearchParams("chapterIds[]");
                }
              }}
              onClear={() => {
                setSelectedChapterIds([]);
                deleteSearchParams("chapterIds[]");
              }}
              className="w-full"
              disabled={selectedSectionIds.length === 0 || chaptersLoading || chapters.length === 0}
            />
          </div>
        </TableControls>
      )}
      columns={columns}
      hasActiveFilters={activeFilterCount > 0}
      renderFilteredEmptyState={() => (
        <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="store" />
      )}
      renderFooter={() => (
        <TableFooter
          pagination={createFooterPagination(data?.facilities)}
          {...(isMergePurgeEnabled && bulkSelectionProps)}
        />
      )}
    />
  );
}
